import { Button } from '@/components/shadcn/button'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { Suspense, useCallback } from 'react'
import AddCollaborationChannelFormV2 from '../Forms/AddCollaborationChannelFormV2'
import { useCurrentApplication } from '../useCurrentApplication'
import { ChannelCardActionsMenu, CollaborationChannelCard } from './CollaborationChannelCard'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { EmptyState } from '@/components/ui/EmptyState'

export const AppCollaborationChannelsSection = () => {
  const { applicationId, appName } = useCurrentApplication()
  const { getCollaborationApiGetCollaborationChannels } = useRestClient()

  const invalidateCollaborationChannels = useCallback(() => {
    getCollaborationApiGetCollaborationChannels.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getCollaborationApiGetCollaborationChannels])

  return (
    <SectionContainer
      title="Collaboration channels"
      action={
        <AddCollaborationChannelFormV2
          sheetTitle={`${appName} > Add collaboration channel`}
          sheetDescription="Add a new collaboration channel to your application."
          sheetTriggerTooltip="Add collaboration channel"
          onSubmit={invalidateCollaborationChannels}
          sheetTrigger={
            <Button variant="ghost" size="icon">
              <IconPlusOutline />
              <span className="sr-only">Add Collaboration Channel</span>
            </Button>
          }
        />
      }
    >
      <QueryErrorBoundary errorMessage="Failed to fetch collaboration channels">
        <Suspense fallback={<LoadingComponent text="Loading collaboration channels..." />}>
          <AppCollaborationChannelsList />
        </Suspense>
      </QueryErrorBoundary>
    </SectionContainer>
  )
}

function AppCollaborationChannelsList() {
  const { applicationId, appName } = useCurrentApplication()
  const { getCollaborationApiGetCollaborationChannels } = useRestClient()

  const { data: collaborationChannels } = getCollaborationApiGetCollaborationChannels.useSuspenseQuery({
    path: { applicationId },
  })

  const invalidateCollaborationChannels = useCallback(() => {
    getCollaborationApiGetCollaborationChannels.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getCollaborationApiGetCollaborationChannels])

  return (
    <>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
        {collaborationChannels?.map((channel) => {
          // Parse JSON hashtags array from API
          let hashtags: string[] = []
          try {
            hashtags = channel.hashtags ? JSON.parse(channel.hashtags) : []
            // Ensure hashtags is an array
            if (!Array.isArray(hashtags)) {
              hashtags = []
            }
          } catch (_error) {
            console.warn('Failed to parse hashtags JSON:', channel.hashtags)
            hashtags = []
          }
          return (
            <CollaborationChannelCard
              key={channel.id}
              keyValue={channel.key || ''}
              value={channel.value || ''}
              hashtags={hashtags}
              emailsEnabled={channel.emailsEnabled}
              cardMenu={<ChannelCardActionsMenu channel={channel} onUpdate={invalidateCollaborationChannels} />}
            />
          )
        })}
      </div>
      {(!collaborationChannels || collaborationChannels.length === 0) && (
        <EmptyState
          title="No collaboration channels found."
          description="Click the + button above to add your first channel."
          actions={
            <AddCollaborationChannelFormV2
              sheetTitle={`${appName} > Add collaboration channel`}
              sheetDescription="Add a new collaboration channel to your application."
              sheetTriggerTooltip="Add collaboration channel"
              onSubmit={invalidateCollaborationChannels}
              sheetTrigger={
                <Button variant="outline" size="sm">
                  <IconPlusOutline />
                  <span className="ml-2">Add Collaboration Channel</span>
                </Button>
              }
            />
          }
        />
      )}
    </>
  )
}
